/**
 * CookieConsentContext — tracks the user's cookie preferences and exposes a
 * small API for reading / updating them.
 *
 * Categories:
 *   - essential   Always true. Required for sign-in, CSRF protection, etc.
 *                 Not user-configurable. Does not require consent under
 *                 the ePrivacy Directive.
 *   - preferences Remember UI choices (theme, language, reader font).
 *   - analytics   PostHog. Off until the user opts in.
 *
 * Persistence:
 *   The decision is stored in localStorage under CONSENT_STORAGE_KEY with a
 *   version tag. If we ever add a new category, bump CONSENT_VERSION and stale
 *   records will be treated as "not yet decided" so the banner reappears.
 *
 *   The consent record itself is a strictly-necessary cookie/storage item —
 *   without it we can't remember that the user declined. Storing it does not
 *   require consent under ePrivacy Art. 5(3) ("strictly necessary for the
 *   provision of a service explicitly requested").
 *
 * Global Privacy Control:
 *   If the browser sends `navigator.globalPrivacyControl === true` and the
 *   user has not made an explicit choice, we treat analytics as opted-out
 *   (CCPA-compliant). The user can still override via the preferences modal.
 *
 * PostHog integration:
 *   Analytics callers must check `useCookieConsent().categories.analytics`
 *   before firing events. This context also calls posthog.opt_in_capturing()
 *   / posthog.opt_out_capturing() whenever the analytics flag changes.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import posthog from 'posthog-js';

const CONSENT_STORAGE_KEY = 'vellum_consent_v1';
const CONSENT_VERSION = 1;

export interface ConsentCategories {
  essential: true; // literal — not user-configurable
  preferences: boolean;
  analytics: boolean;
}

interface StoredConsent {
  version: number;
  decidedAt: string; // ISO timestamp
  categories: Omit<ConsentCategories, 'essential'>;
}

export interface CookieConsentApi {
  /** Has the user made an explicit choice yet? (drives banner visibility) */
  decided: boolean;
  /** Current consent state — essential is always true. */
  categories: ConsentCategories;
  /** Accept all categories. */
  acceptAll: () => void;
  /** Reject everything except strictly-necessary. */
  rejectNonEssential: () => void;
  /** Save an explicit category choice (used by the preferences modal). */
  setCategories: (partial: Partial<Omit<ConsentCategories, 'essential'>>) => void;
  /** Open the preferences modal. Re-exported here so any component can trigger it. */
  openPreferences: () => void;
  /** Internal: used by the modal to close itself. */
  isPreferencesOpen: boolean;
  closePreferences: () => void;
}

const CookieConsentContext = createContext<CookieConsentApi | null>(null);

// ── Helpers ──────────────────────────────────────────────────────────────────

function readStored(): StoredConsent | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(categories: Omit<ConsentCategories, 'essential'>): void {
  try {
    const record: StoredConsent = {
      version: CONSENT_VERSION,
      decidedAt: new Date().toISOString(),
      categories,
    };
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage unavailable (private browsing, quota). Silently ignore —
    // the banner will reappear on next visit, which is the safe default.
  }
}

/** Detect a Global Privacy Control signal. Safe on SSR / older browsers. */
function hasGpcSignal(): boolean {
  if (typeof navigator === 'undefined') return false;
  // Non-standard property, not in lib.dom yet.
  return (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
}

// ── Provider ─────────────────────────────────────────────────────────────────

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [stored, setStored] = useState<StoredConsent | null>(() => readStored());
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  const gpc = hasGpcSignal();

  // Compose the live categories value. If the user has decided, use their
  // choice; otherwise default to everything off (with a GPC override that
  // also forces analytics off).
  const categories: ConsentCategories = useMemo(() => {
    if (stored) {
      return {
        essential: true,
        preferences: stored.categories.preferences,
        analytics: stored.categories.analytics,
      };
    }
    return {
      essential: true,
      preferences: false,
      analytics: false && !gpc, // explicit: analytics stays false pre-decision
    };
  }, [stored, gpc]);

  // Push analytics opt-in/out to PostHog whenever the flag changes.
  useEffect(() => {
    try {
      if (categories.analytics) {
        posthog.opt_in_capturing();
      } else {
        posthog.opt_out_capturing();
      }
    } catch {
      // PostHog not initialised (no key set). No-op.
    }
  }, [categories.analytics]);

  const persist = useCallback((next: Omit<ConsentCategories, 'essential'>) => {
    writeStored(next);
    setStored({
      version: CONSENT_VERSION,
      decidedAt: new Date().toISOString(),
      categories: next,
    });
  }, []);

  const acceptAll = useCallback(() => {
    persist({ preferences: true, analytics: true });
    setIsPreferencesOpen(false);
  }, [persist]);

  const rejectNonEssential = useCallback(() => {
    persist({ preferences: false, analytics: false });
    setIsPreferencesOpen(false);
  }, [persist]);

  const setCategories = useCallback(
    (partial: Partial<Omit<ConsentCategories, 'essential'>>) => {
      persist({
        preferences: partial.preferences ?? categories.preferences,
        analytics: partial.analytics ?? categories.analytics,
      });
    },
    [persist, categories.preferences, categories.analytics],
  );

  const value: CookieConsentApi = useMemo(
    () => ({
      decided: stored !== null,
      categories,
      acceptAll,
      rejectNonEssential,
      setCategories,
      openPreferences: () => setIsPreferencesOpen(true),
      isPreferencesOpen,
      closePreferences: () => setIsPreferencesOpen(false),
    }),
    [stored, categories, acceptAll, rejectNonEssential, setCategories, isPreferencesOpen],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useCookieConsent(): CookieConsentApi {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within <CookieConsentProvider>');
  }
  return ctx;
}

/**
 * Non-hook access for modules that fire analytics outside React (e.g. the
 * apiClient interceptor). Returns false when storage is empty / corrupt so
 * analytics defaults to "off" until the user opts in.
 */
export function hasAnalyticsConsent(): boolean {
  const stored = readStored();
  return stored?.categories.analytics === true;
}
